import { useEffect, useState, type CSSProperties } from "react";
import {
  PageHeader, KindChip, Icon, ghostBtn, primaryBtn, smallBtn,
  hdrCell, cardWrap,
} from "@/components/admin-ui";
import type { KindKey } from "@/components/admin-ui";
import { Modal, ConfirmDialog, useToast, inputBase } from "@/components/ui-prims";
import { useTagActions, useTags, type TagWire } from "@/lib/tags-hooks";

// Topics & tags — the curated tag catalogue. Agents pick from this
// list when composing an article; renaming or merging a tag here
// updates every article's tags[] in one shot.

type EditState =
  | { kind: "create" }
  | { kind: "rename"; tag: TagWire };

type MergeState =
  | { kind: "merge"; from?: TagWire };

const TAG_KINDS: KindKey[] = ["URA", "Policy", "Market", "Brief", "Guide"];

export function TopicsPage() {
  const { tags, refresh } = useTags();
  const actions = useTagActions();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const [edit, setEdit] = useState<EditState | null>(null);
  const [merge, setMerge] = useState<MergeState | null>(null);
  const [archiving, setArchiving] = useState<TagWire | null>(null);

  async function run<T>(key: string, fn: () => Promise<T>): Promise<boolean> {
    if (busy) return false;
    setBusy(key);
    try {
      await fn();
      refresh();
      return true;
    } catch (e) {
      console.warn(`tag action failed: ${key}`, e);
      const msg = e instanceof Error ? e.message : String(e);
      toast(`Action failed: ${msg}`, { tone: "error" });
      return false;
    } finally {
      setBusy(null);
    }
  }

  const submitEdit = async (label: string, kind: KindKey) => {
    if (!edit) return;
    const ok = edit.kind === "create"
      ? await run("create", () => actions.create(label, kind))
      : await run(`rename-${edit.tag.tag}`, () => actions.rename(edit.tag.tag, label));
    if (ok) {
      toast(edit.kind === "create" ? `Tag "${label}" created` : `Renamed to "${label}"`, { tone: "success" });
      setEdit(null);
    }
  };

  const submitMerge = async (from: string, into: string) => {
    const ok = await run(`merge-${from}`, () => actions.merge(from, into));
    if (ok) {
      toast(`Merged into "${into}"`, { tone: "success" });
      setMerge(null);
    }
  };

  const confirmArchive = async () => {
    if (!archiving) return;
    const ok = await run(`archive-${archiving.tag}`, () => actions.archive(archiving.tag, true));
    if (ok) {
      toast(`Tag "${archiving.label}" archived`, { tone: "success" });
      setArchiving(null);
    }
  };

  const restoreTag = async (t: TagWire) => {
    const ok = await run(`unarchive-${t.tag}`, () => actions.archive(t.tag, false));
    if (ok) toast(`Tag "${t.label}" restored`, { tone: "success" });
  };

  return (
    <>
      <PageHeader
        kicker="Editorial"
        title="Topics & tags"
        sub="The taxonomy buyers see in Newsroom filters and agents pick from in the composer. Merge or rename here to keep the surface clean."
        actions={
          <>
            <button style={ghostBtn} type="button" onClick={() => setMerge({ kind: "merge" })}>
              Merge tags…
            </button>
            <button style={primaryBtn} type="button" onClick={() => setEdit({ kind: "create" })}>
              {Icon.plus} New tag
            </button>
          </>
        }
      />

      <div style={cardWrap}>
        <div style={hdr}>
          <div>Tag</div>
          <div>Kind</div>
          <div style={{ textAlign: "right" }}>Articles</div>
          <div style={{ textAlign: "right" }}>Leads 30d</div>
          <div style={{ textAlign: "right" }}>Status</div>
          <div style={{ textAlign: "right" }}>Actions</div>
        </div>
        {tags.map((t) => {
          const isArchived = !!t.archived_at;
          return (
            <div key={t.tag} style={row} className="ops-row">
              <div
                style={{
                  fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500,
                  color: isArchived ? "var(--ink-3)" : "var(--ink)",
                  letterSpacing: "-0.01em",
                  textDecoration: isArchived ? "line-through" : "none",
                }}
                title={t.tag}
              >
                {t.label}
              </div>
              <div><KindChip kind={t.kind} /></div>
              <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>{t.count}</div>
              <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent)", fontWeight: 500 }}>
                {t.leads}
              </div>
              <div
                style={{
                  textAlign: "right", fontFamily: "var(--font-ui)", fontSize: 11,
                  color: isArchived ? "#8a5a00" : "var(--ink-3)",
                }}
              >
                {isArchived ? "Archived" : "Active"}
              </div>
              <div style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 6 }}>
                <button style={smallBtn} type="button" disabled={!!busy} onClick={() => setEdit({ kind: "rename", tag: t })}>
                  Rename
                </button>
                <button style={smallBtn} type="button" disabled={!!busy} onClick={() => setMerge({ kind: "merge", from: t })}>
                  Merge
                </button>
                <button
                  style={smallBtn}
                  type="button"
                  disabled={!!busy}
                  onClick={() => (isArchived ? restoreTag(t) : setArchiving(t))}
                  title={isArchived ? "Restore tag" : "Archive tag"}
                >
                  {isArchived ? "Restore" : Icon.archive}
                </button>
              </div>
            </div>
          );
        })}
        {tags.length === 0 && (
          <div
            style={{
              padding: "48px 0", textAlign: "center",
              fontFamily: "var(--font-serif)", fontStyle: "italic",
              color: "var(--ink-3)", fontSize: 14,
            }}
          >
            No tags yet. Click "New tag" to create one — agents can only pick from this catalogue.
          </div>
        )}
      </div>
      <div style={{ height: 22 }} />

      <TagModal
        edit={edit}
        onSubmit={submitEdit}
        onCancel={() => setEdit(null)}
        busy={!!busy}
      />

      <MergeTagsModal
        merge={merge}
        tags={tags}
        onSubmit={submitMerge}
        onCancel={() => setMerge(null)}
        busy={!!busy}
      />

      <ConfirmDialog
        open={!!archiving}
        title={`Archive "${archiving?.label ?? ""}"?`}
        body="Articles keep the tag string, but agents can no longer pick it from the composer. You can restore the tag later if you change your mind."
        confirmLabel="Archive"
        confirmTone="danger"
        busy={busy?.startsWith("archive-") ?? false}
        onConfirm={confirmArchive}
        onCancel={() => setArchiving(null)}
      />
    </>
  );
}

/* ─── TagModal (Create / Rename) ─────────────────────────────────── */

function TagModal({
  edit, onSubmit, onCancel, busy,
}: {
  edit: EditState | null;
  onSubmit: (label: string, kind: KindKey) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<KindKey>("Brief");

  useEffect(() => {
    if (!edit) return;
    if (edit.kind === "create") {
      setLabel("");
      setKind("Brief");
    } else {
      setLabel(edit.tag.label);
      setKind(edit.tag.kind);
    }
  }, [edit]);

  const isCreate = edit?.kind === "create";
  const canSubmit = label.trim().length >= 2 && (!edit || edit.kind === "create" || label !== edit.tag.label || kind !== edit.tag.kind);

  return (
    <Modal
      open={!!edit}
      onClose={busy ? () => {} : onCancel}
      title={isCreate ? "New tag" : `Rename "${edit?.kind === "rename" ? edit.tag.label : ""}"`}
      width={460}
      footer={
        <>
          <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: !canSubmit || busy ? 0.5 : 1 }}
            disabled={!canSubmit || busy}
            onClick={() => onSubmit(label.trim(), kind)}
          >
            {busy ? "Saving…" : isCreate ? "Create tag" : "Save"}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Label">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. URA Master Plan 2025"
            maxLength={64}
            style={inputBase}
            autoFocus
          />
          <div style={hintStyle}>What buyers see in the Newsroom filter chip.</div>
        </Field>

        {isCreate && (
          <Field label="Kind">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as KindKey)}
              style={inputBase}
            >
              {TAG_KINDS.map(k => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
            <div style={hintStyle}>Sets the chip colour. Used to group tags by editorial section.</div>
          </Field>
        )}
      </div>
    </Modal>
  );
}

/* ─── MergeTagsModal ─────────────────────────────────────────────── */

function MergeTagsModal({
  merge, tags, onSubmit, onCancel, busy,
}: {
  merge: MergeState | null;
  tags: TagWire[];
  onSubmit: (from: string, into: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [from, setFrom] = useState("");
  const [into, setInto] = useState("");

  useEffect(() => {
    if (!merge) return;
    setFrom(merge.from?.tag ?? "");
    setInto("");
  }, [merge]);

  // Only show active tags as candidates; archived tags shouldn't be a
  // merge target.
  const candidates = tags.filter(t => !t.archived_at);
  const fromTag = candidates.find(t => t.tag === from);
  const intoTag = candidates.find(t => t.tag === into);
  const canSubmit = !!fromTag && !!intoTag && fromTag.tag !== intoTag.tag;

  return (
    <Modal
      open={!!merge}
      onClose={busy ? () => {} : onCancel}
      title="Merge tags"
      width={520}
      footer={
        <>
          <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: !canSubmit || busy ? 0.5 : 1 }}
            disabled={!canSubmit || busy}
            onClick={() => fromTag && intoTag && onSubmit(fromTag.tag, intoTag.tag)}
          >
            {busy ? "Merging…" : "Merge tags"}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Merge from">
          <select
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={inputBase}
            autoFocus
          >
            <option value="">— pick a tag —</option>
            {candidates.map(t => (
              <option key={t.tag} value={t.tag}>{t.label} · {t.count} article{t.count === 1 ? "" : "s"}</option>
            ))}
          </select>
        </Field>

        <Field label="Merge into">
          <select
            value={into}
            onChange={(e) => setInto(e.target.value)}
            style={inputBase}
          >
            <option value="">— pick a tag —</option>
            {candidates
              .filter(t => t.tag !== from)
              .map(t => (
                <option key={t.tag} value={t.tag}>{t.label} · {t.count} article{t.count === 1 ? "" : "s"}</option>
              ))}
          </select>
        </Field>

        {fromTag && intoTag && (
          <div style={previewStyle}>
            All <strong>{fromTag.count}</strong> article{fromTag.count === 1 ? "" : "s"} tagged{" "}
            <strong>"{fromTag.label}"</strong> will move to <strong>"{intoTag.label}"</strong>.
            The source tag is removed. <em>This cannot be undone.</em>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const cols = "1.4fr 100px 90px 110px 90px 220px";
const hdr: CSSProperties = {
  display: "grid", gridTemplateColumns: cols, gap: 12,
  padding: "10px 16px", borderBottom: "1px solid var(--rule)",
  background: "var(--surface-2)", ...hdrCell,
};
const row: CSSProperties = {
  display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "center",
  padding: "12px 16px", borderBottom: "1px solid var(--rule)",
};
const hintStyle: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)",
};
const previewStyle: CSSProperties = {
  padding: "10px 12px", borderRadius: 6,
  background: "rgba(138,90,0,0.08)", border: "1px solid rgba(138,90,0,0.15)",
  color: "#5a3e00", fontSize: 12.5, lineHeight: 1.55,
};
